import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { generatePaymentFormLink } from 'utils';

import {
  AccountManager,
  btag,
  constants,
  isUserActionNeedPhotoVerification,
  NotificationManager,
  PaymentV2Api,
  PhotoVerificationAction,
  pushToDataLayer,
  useApplicationState,
  useLabel,
  useTranslation,
  useVerificationCheck,
} from '@atlas/core';
import { onWithdrawalSubmit, PREDEFINED_FORMS } from '@atlas/react-forms';
import { DefaultPopup, Icon, Label, Tooltip } from '@atlas/ui';

import { ACCOUNT_NUMBERS_POPUP_TYPES, urlSeparator } from 'common/constants';
import FormMobile from 'common/Form/FormMobile';
import ModalPortal from 'common/ModalPortal/ModalPortal';
import PaymentMethodItem from 'components/ClientProfile/Payment/Deposit/PaymentMethodItem';
import PaymentFormFooter from 'components/ClientProfile/Payment/PaymentFormFooter';
import { useFaceCheck } from 'components/ClientProfile/useFaceCheck';
import SeoPageText from 'components/SeoPage/SeoPageText';
import MoneyValueCustomCurrency from 'src/components/MoneyValueCustomCurrency/MoneyValueCustomCurrency';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import CommissionsBlock from '../CommissionsBlock';
import { PaymentProvider } from '../PaymentProvider';
import { isPartialVerification, isPaymentAllowed } from '../utils';

const TOOLTIP_ICON_SIZE = 13;
const GPAY_METHOD = 'gpay';
const PAYMAYA_METHOD = 'paymaya';
const PAYSTACK_METHOD = 'paystack';
const BITCOIN_METHOD = 'coinspaid';
const BITCOIN_METHODV2 = 'coinspaidV2';
const ALFACOIN_METHOD = 'alfacoins';
const BITCOIN_METHODV2_WITH_TAG = 'coinspaidV2WithTag';
const APEXPAY_METHOD = 'apexpay';
const SANALBANK_METHOD = 'sanalbank';
const SPEI = 'starspaySpeiWithdrawal';
const BILL_LINE_SPEI = 'billlineSpeiWithdrawal';
const INTERKASSA_CARD = 'interkassa_nova_card';

const PIX_POPUP_METHODS = ['pagatudo', 'zrobank', 'zenetpay', 'pagstar'];
const INTERKASSA_POPUP_METHODS = {
  community_bank: ['account_number', 'document_id', 'iban', 'phone'],
  havale: ['iban'],
  jeton: ['account_number'],
  mefete: ['account_number', 'document_id'],
  nova_banking: ['document_id', 'bank_identity', 'iban'],
  papara: ['account_number', 'document_id'],
  paybol: ['account_number'],
  payfix: ['account_number'],
  pep: ['account_number'],
  vega: ['account_number', 'ifsc'],
};
const ATLAS_TRANSFER = 'atlasTransferWithdrawal';

const REDIRECT_URL_POPUPS = {
  [GPAY_METHOD]: { message: 'gpay', type: 'gpay' },
  [PAYMAYA_METHOD]: { message: 'paymaya', type: 'paymaya' },
};

const showRedirectUrlPopup = redirectUrl => {
  for (const [method, config] of Object.entries(REDIRECT_URL_POPUPS)) {
    if (redirectUrl.startsWith(method)) {
      NotificationManager.display({
        data: { redirectUrl },
        message: config.message,
        type: config.type,
      });
      return true;
    }
  }
  return false;
};

const parseWithdrawalData = (redirectUrl, defaultAmount, defaultCurrency) => {
  let withdrawalAmount = defaultAmount;
  let withdrawalCurrency = defaultCurrency;

  if (redirectUrl) {
    const parsedData = qs.parse(redirectUrl) || {};
    if (parsedData.amount) {
      withdrawalAmount = parsedData.amount;
    }
    if (parsedData.currency) {
      withdrawalCurrency = parsedData.currency;
    }
  }

  return { withdrawalAmount, withdrawalCurrency };
};

const isPopupWithCashierFlow = (processingType, operationFlow) =>
  operationFlow === constants.PAYMENT_OPERATION_FLOW.INTERNAL_CASHIER ||
  (processingType === constants.PAYMENT_PROCESSING.POPUP &&
    operationFlow === constants.PAYMENT_OPERATION_FLOW.EXTERNAL_CASHIER);

const shouldShowWithdrawalNotification = (processingType, operationFlow) =>
  processingType !== 'NONE' || operationFlow === constants.PAYMENT_OPERATION_FLOW.INTERNAL_CASHIER;

let savedAmount;

const WithdrawalPayment = ({ paymentMethod = null }) => {
  const { clientId } = AccountManager.account;
  const { url } = useRouteMatch();
  const backUrlArray = url.split(urlSeparator);
  backUrlArray.pop();
  const history = useHistory();
  const translate = useTranslation();
  const scrollElement = useRef(null);
  const [, setRefIsReady] = useState(false);

  const applicationState = useApplicationState();
  const { account, bankAccounts } = applicationState;
  const { docNumber, firstName, kycVerificationDate, lastName, partiallyUnderRegulation } = account;
  const ref = useRef(null);
  const {
    showSelfRegulationPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopup,
    showSpentLimitPopupIfNeeded,
  } = useSelfRegulationContext();

  const { wallet } = useApplicationState();
  const loadingNotificationId = useRef();
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = wallet.detailedBalance;
  const totalBalance = BONUS + CASH + FROZEN;
  const availableBalanceTooltipMessage = useLabel('available_balance_tooltip_message', {
    availableNow: CASH,
    locked: FROZEN,
  });

  const { id } = paymentMethod;

  const [dataForFaceCheck, setDataForFaceCheck] = useState(null);

  const isShowVerificationPopup =
    partiallyUnderRegulation &&
    !kycVerificationDate &&
    isUserActionNeedPhotoVerification(account, PhotoVerificationAction.WITHDRAWAL);

  const showPixPopups = useCallback(({ paymentMethod, paymentName, pmId, redirectUrl }) => {
    if (paymentName === 'starspay(crypto)') {
      return NotificationManager.display({
        data: {
          addressKey: 'cryptoUserAddress',
          paymentMethod,
          redirectUrl,
          withoutInfo: true,
        },
        message: 'withdrawalBitcoinV2',
        type: 'withdrawalBitcoinV2',
      });
    }

    if (redirectUrl.startsWith('pixWithdrawalNoEvp')) {
      return NotificationManager.display({
        data: {
          formAction: 'payment/v2/withdraw:create',
          isNewImplementation: true,
          paymentMethod,
          pmId,
          redirectUrl,
        },
        message: 'WithdrawalPixNoEvp',
        type: 'WithdrawalPixNoEvp',
      });
    }

    if (redirectUrl.startsWith('pixWithdrawal')) {
      return NotificationManager.display({
        data: {
          formAction: 'payment/v2/withdraw:create',
          isNewImplementation: true,
          paymentMethod,
          pmId,
          redirectUrl,
        },
        message: 'withdrawalPix',
        type: 'withdrawalPix',
      });
    }

    if (paymentName.startsWith('pix')) {
      if (paymentName.includes('starspay')) {
        return NotificationManager.display({
          data: { formAction: 'payments/starspay/withdraw', paymentMethod, redirectUrl },
          message: 'withdrawalPix',
          type: 'withdrawalPix',
        });
      }

      if (paymentName.includes('pagfast')) {
        return NotificationManager.display({
          data: {
            formAction: 'pagfast-adapter/v1/payment:authorize',
            paymentMethod,
            redirectUrl,
          },
          message: 'withdrawalPix',
          type: 'withdrawalPix',
        });
      }

      if (paymentName.includes('paybrokers')) {
        return NotificationManager.display({
          data: {
            formAction: 'paybrokers-adapter/v1/payment:create',
            paymentMethod,
            redirectUrl,
          },
          message: 'withdrawalPix',
          type: 'withdrawalPix',
        });
      }

      if (PIX_POPUP_METHODS.some(method => paymentName.includes(method))) {
        return NotificationManager.display({
          data: {
            formAction: 'payment/v2/withdraw:create',
            isNewImplementation: true,
            paymentMethod,
            pmId,
            redirectUrl,
          },
          message: 'withdrawalPix',
          type: 'withdrawalPix',
        });
      }
    }
  }, []);

  const showOtherPopups = useCallback(
    ({ paymentFormName, paymentMethod, pmId, publicPaymentId, redirectUrl, redirectUrlName }) => {
      if (
        Object.keys(INTERKASSA_POPUP_METHODS).some(
          method => `interkassa_${method}` === String(redirectUrlName),
        )
      ) {
        const paymentName = redirectUrlName.replace('interkassa_', '');
        return NotificationManager.display({
          data: {
            fields: INTERKASSA_POPUP_METHODS[paymentName],
            formAction: 'payment/v2/withdraw:create',
            name: paymentName,
            paymentMethod,
            pmId,
            redirectUrl,
          },
          message: 'withdrawalInterkassa',
          type: 'withdrawalInterkassa',
        });
      }

      if (redirectUrl.startsWith('directa24NG')) {
        return NotificationManager.display({
          data: { formAction: 'payment/v2/withdraw:create', paymentMethod, pmId, redirectUrl },
          message: 'withdrawalDirectaPopup',
          type: 'withdrawalDirectaPopup',
        });
      }

      const isBillLineSpeiWithdrawal = redirectUrl.startsWith(BILL_LINE_SPEI);
      if (isBillLineSpeiWithdrawal || redirectUrl.startsWith(SPEI)) {
        return NotificationManager.display({
          data: {
            paymentMethod,
            pmId,
            publicPaymentId,
            redirectUrl,
            withValidation: isBillLineSpeiWithdrawal,
          },
          message: 'speiWithdrawal',
          type: 'speiWithdrawal',
        });
      }

      if (redirectUrl.startsWith(INTERKASSA_CARD)) {
        return NotificationManager.display({
          data: {
            paymentMethod,
            pmId,
            publicPaymentId,
            redirectUrl,
          },
          message: 'withdrawalInterkassaCard',
          type: 'withdrawalInterkassaCard',
        });
      }

      if (paymentFormName.startsWith(ALFACOIN_METHOD)) {
        const withTag = redirectUrl.startsWith('cryptoWithdrawalWithTag');
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl, withTag },
          message: 'withdrawalAlfacoin',
          type: 'withdrawalAlfacoin',
        });
      }

      const data = { paymentMethod, pmId, publicPaymentId, redirectUrl };

      if (
        paymentFormName === 'prontopaga' &&
        redirectUrl.startsWith('prontopagaTransferWithdrawal')
      ) {
        return NotificationManager.display({
          data,
          message: 'withdrawalProntopaga',
          type: 'withdrawalProntopaga',
        });
      }

      if (redirectUrl.startsWith('interkassa_vega_eubanking')) {
        return NotificationManager.display({
          data,
          message: 'withdrawalInterkassaEuro',
          type: 'withdrawalInterkassaEuro',
        });
      }

      if (redirectUrl.startsWith('interkassa_magnetar')) {
        return NotificationManager.display({
          data,
          message: 'withdrawalMagnetar',
          type: 'withdrawalMagnetar',
        });
      }

      if (paymentFormName === 'kushki') {
        return NotificationManager.display({
          data,
          message: 'withdrawalKushki',
          type: 'withdrawalKushki',
        });
      }

      if (paymentFormName === 'tonder') {
        return NotificationManager.display({
          data,
          message: 'withdrawalTonder',
          type: 'withdrawalTonder',
        });
      }

      if (paymentFormName === 'payretailers') {
        return NotificationManager.display({
          data,
          message: 'withdrawalPayretailers',
          type: 'withdrawalPayretailers',
        });
      }
    },
    [],
  );

  const showPartUnCategorizedPopups = useCallback(
    ({ operationFlow, paymentMethod, paymentName, processingType, redirectUrl }) => {
      if (paymentName.startsWith('pix')) {
        if (isPopupWithCashierFlow(processingType, operationFlow)) {
          return;
        }

        const data = {
          headerTitle: 'withdrawal_header_title_success',
          text: 'withdrawal_text_success',
          title: 'withdrawal_title_success',
        };

        if (paymentName.includes('paybrokers')) {
          data.headerTitle = 'withdrawal_pix_paybrokers_header_title_success';
          data.text = 'withdrawal_pix_paybrokers_text_success';
        }

        return NotificationManager.display({
          data,
          message: 'success',
          type: 'success',
        });
      }

      if (paymentName.includes(PAYSTACK_METHOD)) {
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl },
          message: 'withdrawalPaystack',
          type: 'withdrawalPaystack',
        });
      }

      if (redirectUrl.startsWith(BITCOIN_METHODV2)) {
        const withTag = redirectUrl.startsWith(BITCOIN_METHODV2_WITH_TAG);
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl, withTag },
          message: 'withdrawalBitcoinV2',
          type: 'withdrawalBitcoinV2',
        });
      }

      if (redirectUrl.startsWith(BITCOIN_METHOD)) {
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl },
          message: 'withdrawalBitcoin',
          type: 'withdrawalBitcoin',
        });
      }

      if (redirectUrl.startsWith(APEXPAY_METHOD)) {
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl },
          message: 'withdrawalApexpay',
          type: 'withdrawalApexpay',
        });
      }

      if (redirectUrl.startsWith(SANALBANK_METHOD)) {
        return NotificationManager.display({
          data: { paymentMethod, redirectUrl },
          message: 'withdrawalSanalbank',
          type: 'withdrawalSanalbank',
        });
      }
    },
    [],
  );

  const showUnCategorizedPopups = useCallback(
    ({
      amount,
      displayOption,
      isShowVerificationPopup,
      operationFlow,
      paymentFormName,
      paymentId,
      paymentMethod,
      paymentName,
      pmId,
      processingType,
      publicPaymentId,
      redirectUrl,
    }) => {
      if (
        paymentFormName === 'retail' &&
        processingType === constants.PAYMENT_PROCESSING.REDIRECT
      ) {
        return NotificationManager.display({
          data: { paymentId, redirectUrl },
          message: 'withdrawalCashdesk',
          type: 'withdrawalCashdesk',
        });
      }

      showRedirectUrlPopup(redirectUrl);

      if (redirectUrl.startsWith(ATLAS_TRANSFER)) {
        return NotificationManager.display({
          data: { paymentMethod, paymentName, pmId, publicPaymentId, redirectUrl },
          message: 'atlasTransferWithdrawal',
          type: 'atlasTransferWithdrawal',
        });
      }

      if (processingType === constants.PAYMENT_PROCESSING.REDIRECT && redirectUrl) {
        if (displayOption === 'NO_IFRAME') {
          window.location.href = redirectUrl;
          return;
        }

        return NotificationManager.display({
          data: { paymentUrl: redirectUrl },
          message: 'Withdrawal',
          type: 'withdrawalPopup',
        });
      }

      if (isShowVerificationPopup) return;

      if (isPopupWithCashierFlow(processingType, operationFlow)) {
        const { withdrawalAmount, withdrawalCurrency } = parseWithdrawalData(
          redirectUrl,
          amount,
          wallet.currency,
        );

        return NotificationManager.display({
          data: {
            amount: withdrawalAmount,
            currency: withdrawalCurrency,
            paymentId: paymentId || publicPaymentId,
            paymentMethod,
          },
          message: 'successfulWithdrawal',
          type: 'successfulWithdrawal',
        });
      }

      if (shouldShowWithdrawalNotification(processingType, operationFlow)) {
        NotificationManager.notify({
          message: 'withdrawal_request_created',
          status: 'success',
          type: 'notification',
        });
      }
    },
    [wallet.currency],
  );

  const submitHandler = useCallback(
    data => {
      const {
        displayOption,
        formData: { additionalParams = {} } = {},
        operationFlow,
        paymentFormName = '',
        paymentId,
        pmId,
        processingType,
        publicPaymentId,
        redirectUrl = '',
      } = data;
      const { name = '' } = paymentMethod;
      const paymentName = name.toLowerCase();
      const redirectUrlName = qs.parseUrl(redirectUrl)?.url;
      const formId = paymentMethod.paymentForms[0]?.formId;
      const amount = savedAmount;

      if (processingType === constants.PAYMENT_PROCESSING.FORM && formId) {
        return NotificationManager.display({
          data: {
            amount,
            formId,
            interpolationValues: additionalParams,
            paymentMethod,
            publicPaymentId,
            type: constants.PAYMENT_OPERATION.WITHDRAWAL,
          },
          message: 'paymentsForm',
          type: 'paymentsForm',
        });
      }

      if (processingType === constants.PAYMENT_PROCESSING.FORM) {
        window.open(generatePaymentFormLink(data));
      }

      if (processingType === constants.PAYMENT_PROCESSING.POPUP) {
        const isShowPixPopup = showPixPopups({
          paymentMethod,
          paymentName,
          pmId,
          redirectUrl,
        });

        if (isShowPixPopup) return;

        const isShowOtherPopup = showOtherPopups({
          paymentFormName,
          paymentMethod,
          pmId,
          publicPaymentId,
          redirectUrl,
          redirectUrlName,
        });

        if (isShowOtherPopup) return;
      }

      const isShowUnCategorizedPopup = showPartUnCategorizedPopups({
        operationFlow,
        paymentMethod,
        paymentName,
        processingType,
        redirectUrl,
      });

      if (isShowUnCategorizedPopup) return;

      showUnCategorizedPopups({
        amount,
        displayOption,
        isShowVerificationPopup,
        operationFlow,
        paymentFormName,
        paymentId,
        paymentMethod,
        paymentName,
        pmId,
        processingType,
        publicPaymentId,
        redirectUrl,
      });
    },
    [
      paymentMethod,
      showPartUnCategorizedPopups,
      showUnCategorizedPopups,
      isShowVerificationPopup,
      showPixPopups,
      showOtherPopups,
    ],
  );

  const { isNeedFaceCheck } = useFaceCheck();

  const form = useMemo(
    () => PREDEFINED_FORMS.withdrawalForm(paymentMethod, !!dataForFaceCheck),
    [dataForFaceCheck, paymentMethod],
  );

  const onCreateWithdrawal = () => {
    ref?.current?.setIsLoading(false);

    const notificationId = NotificationManager.display({
      data: {
        headerTitle: 'withdrawal_loading',
        text: 'withdrawal_loading_text',
        title: 'withdrawal_loading_title',
      },
      type: 'paymentLoading',
    });
    loadingNotificationId.current = notificationId;
  };

  const onWithdrawalActionHandler = useCallback(
    async paymentOptions => {
      if (showSpentLimitPopup) {
        await showSpentLimitPopupIfNeeded();
        return;
      }

      if (showSelfRegulationPopup) {
        await showSelfRegulationPopupIfNeeded();
        return;
      }

      if (!paymentOptions.bankAccountInfo) {
        ref.current.setIsLoading(true);
      }

      savedAmount = ref?.current.values.amount;

      const res = await PaymentV2Api.getPaymentData({
        onCreatePayment: onCreateWithdrawal,
        onPaymentStatusError: onWithdrawalStatusError,
        paymentOptions: {
          ...paymentOptions,
          currency: wallet.currency,
        },
        paymentType: PaymentV2Api.PaymentType.WITHDRAWAL,
      });

      NotificationManager.removeNotification(loadingNotificationId.current);

      return res;
    },
    [
      showSelfRegulationPopup,
      showSelfRegulationPopupIfNeeded,
      showSpentLimitPopup,
      showSpentLimitPopupIfNeeded,
      wallet.currency,
    ],
  );

  const onWithdrawalStatusError = (error = {}) => {
    NotificationManager.removeNotification(loadingNotificationId.current);

    NotificationManager.display({
      data: {
        className: 'withdrawal-failed-popup',
        headerTitle: 'withdrawal',
        text: `${error.code ? error.code.toLowerCase() : 'withdrawal_failed_message'}`,
        title: 'withdrawal_failed_title',
      },
      type: 'errorTransaction',
    });
  };

  const accountNumbersPopupShow = useCallback(
    formData => {
      NotificationManager.display({
        data: {
          amount: formData.amount,
          onWithdrawalStatusError,
          paymentAction: onWithdrawalActionHandler,
          paymentMethod,
          paymentSubmit: submitHandler,
          pmId: formData.pmId,
          type: ACCOUNT_NUMBERS_POPUP_TYPES.WITHDRAWAL,
        },
        message: 'accountNumbers',
        type: 'accountNumbers',
      });
    },
    [onWithdrawalActionHandler, paymentMethod, submitHandler],
  );

  const finAccountFieldsPopupShow = useCallback(
    formData => {
      NotificationManager.display({
        data: {
          amount: formData.amount,
          cb: () => {},
          formAction: 'payment/v2/withdraw:create',
          paymentMethod: paymentMethod,
          pmId: formData.pmId,
        },
        message: 'withdrawalInterac',
        type: 'withdrawalInterac',
      });
    },
    [paymentMethod],
  );

  const { verify } = useVerificationCheck({
    docNumber,
    firstName,
    lastName,
    onError: () => {
      NotificationManager.notify({
        message: 'withdrawal_verification_failed',
        status: 'error',
        type: 'notification',
      });
    },
    onSuccess: async () => {
      pushToDataLayer('KYC_completed', { client: { id: clientId } }, btag);
      NotificationManager.notify({
        message: 'withdrawal_verification_completed_successfully',
        status: 'success',
        type: 'notification',
      });

      const isSpentLimitResolved = await showSpentLimitPopupIfNeeded();
      if (isSpentLimitResolved) {
        await showSelfRegulationPopupIfNeeded();
      }
    },
    verificationType: 'facecheck',
  });

  const faceCheckAction = useCallback(async () => {
    try {
      pushToDataLayer('KYC_started', { client: { id: clientId } }, btag);
      await verify();
    } catch (error) {
      NotificationManager.notify({
        message: 'withdrawal_verification_process_failed',
        status: 'error',
        type: 'notification',
      });
    }
  }, [verify, clientId]);

  const action = useMemo(() => {
    const hasAccountNumbersPopup = paymentMethod && paymentMethod.displayAccountNumberSelector;
    const hasFinAccountFields = paymentMethod && paymentMethod.displayFinAccountFields;

    if (isNeedFaceCheck) {
      if (hasAccountNumbersPopup) {
        if (dataForFaceCheck) {
          return faceCheckAction;
        } else {
          return formData => setDataForFaceCheck(formData);
        }
      } else {
        return faceCheckAction;
      }
    }

    if (hasFinAccountFields) {
      return finAccountFieldsPopupShow;
    }

    if (hasAccountNumbersPopup) {
      return accountNumbersPopupShow;
    }

    return onWithdrawalSubmit(onWithdrawalActionHandler);
  }, [
    paymentMethod,
    isNeedFaceCheck,
    onWithdrawalActionHandler,
    dataForFaceCheck,
    faceCheckAction,
    finAccountFieldsPopupShow,
    accountNumbersPopupShow,
  ]);

  const buttonText = useMemo(() => {
    if (!docNumber) return 'withdrawal';
    const displayAccNumber = paymentMethod && paymentMethod.displayAccountNumberSelector;

    if (isNeedFaceCheck && (!displayAccNumber || dataForFaceCheck)) {
      return 'withdrawal_kyc_verify_to_withdraw';
    }

    if (displayAccNumber) {
      return 'withdrawal_kyc_submit_amount';
    }

    return 'withdrawal';
  }, [docNumber, paymentMethod, isNeedFaceCheck, dataForFaceCheck]);

  const renderFormFooter = useMemo(
    () => (
      <>
        <CommissionsBlock operationType={constants.PAYMENT_OPERATION.WITHDRAWAL} paymentId={id} />
        <PaymentFormFooter staticBlockSource="withdrawal-guide" />
      </>
    ),
    [id],
  );

  useEffect(() => {
    const layoutElement = document.querySelector('.payment-method-popup .popup__window');

    if (layoutElement) {
      scrollElement.current = layoutElement;
      setRefIsReady(true); // rerender is needed after setting ref
    }
  }, []);

  useEffect(() => {
    if (
      isPartialVerification(account, 'withdrawal') ||
      !isPaymentAllowed(account, bankAccounts, 'withdrawal', paymentMethod.autofillProhibited)
    ) {
      history.replace(backUrlArray.join(urlSeparator));
      NotificationManager.display({
        data: {
          isPartialRegulation: isPartialVerification(account, 'withdrawal'),
        },
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }
  }, [account, bankAccounts, backUrlArray, history, paymentMethod.autofillProhibited]);

  const infoTextKey = useMemo(() => {
    return `pmid_${paymentMethod?.id}_withdrawal`;
  }, [paymentMethod?.id]);

  return (
    <ModalPortal>
      <DefaultPopup
        className="payment-method-popup"
        headerTitle="Withdrawal"
        headerTitlePosition="left"
        onClose={() => history.push(backUrlArray.join(urlSeparator))}
      >
        <div className="popup__content">
          <div className="profile-payments__block">
            <PaymentMethodItem operation="withdrawal" paymentMethod={paymentMethod} />
          </div>

          {translate[infoTextKey] && (
            <div className="profile-payments__info-text-wrapper">
              <Label className="profile-payments__info-text" message={infoTextKey} />
            </div>
          )}

          <div className="withdrawal-user-balance">
            <div className="withdrawal-user-balance-item">
              <Label
                className="withdrawal-user-balance-label sub-title-xsmall-300-text"
                message="total_balance"
              />
              <MoneyValueCustomCurrency
                className="withdrawal-user-balance-value"
                value={totalBalance}
              />
            </div>

            <div className="withdrawal-user-balance-item">
              <div className="withdrawal-user-balance-label-wrap">
                <Label
                  className="withdrawal-user-balance-label sub-title-xsmall-300-text"
                  message="available_balance"
                />

                <Tooltip
                  message={availableBalanceTooltipMessage}
                  placement="bottom"
                  scrollElement={scrollElement.current}
                  trigger="touch"
                >
                  <Icon
                    height={TOOLTIP_ICON_SIZE}
                    name="notification-info"
                    width={TOOLTIP_ICON_SIZE}
                  />
                </Tooltip>
              </div>

              <MoneyValueCustomCurrency className="withdrawal-user-balance-value" value={CASH} />
            </div>
          </div>

          <div className="form-container form-container-payment">
            <PaymentProvider selectedPayment={paymentMethod}>
              <FormMobile
                action={action}
                additionalValues={{ pmId: id }}
                buttonText={buttonText}
                footerContent={renderFormFooter}
                form={form}
                onSubmit={submitHandler}
                ref={ref}
              />
            </PaymentProvider>
          </div>
          <SeoPageText />
        </div>
      </DefaultPopup>
    </ModalPortal>
  );
};

WithdrawalPayment.propTypes = {
  paymentMethod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default WithdrawalPayment;
